import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Truck, Loader2, RefreshCw, XCircle, FileText, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FROM = {
  first_name: 'Service',
  last_name: 'Expéditions',
  company: 'Respectful Markets',
  email: 'support@respectfulmarkets.com',
  phone: '+33100000000',
  street: '4 boulevard des Capucines',
  city: 'Paris',
  postal_code: '75009',
  country: 'FR',
};

const errText = (error) => {
  const d = error.response?.data?.detail;
  if (!d) return error.message;
  if (typeof d === 'string') return d;
  const errs = (d.errors || []).map((e) => e.code || e.message).join(', ');
  return `${d.message || 'Erreur Boxtal'}${errs ? ` (${errs})` : ''}`;
};

export const AdminBoxtalShipDialog = ({ order, open, onOpenChange, onUpdated }) => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [boxtal, setBoxtal] = useState(null);
  const [tracking, setTracking] = useState(null);

  const [form, setForm] = useState({
    offer_code: '',
    weight_kg: '0.5',
    length_cm: '30',
    width_cm: '20',
    height_cm: '15',
    value_eur: '10',
    category_id: '',
    description: '',
    pickup_point_code: '',
    confirm: false,
  });
  const [fromAddr, setFromAddr] = useState(DEFAULT_FROM);

  useEffect(() => {
    if (!open || !order) return;
    setBoxtal(order.boxtal || null);
    setTracking(order.tracking || null);
    const totalWeight = (order.items || []).reduce((s, it) => s + (it.weight_kg || 0.5) * (it.quantity || 1), 0);
    setForm((f) => ({
      ...f,
      weight_kg: String(Math.max(0.1, totalWeight).toFixed(2)),
      value_eur: String((order.subtotal || order.total || 10).toFixed(2)),
      description: `Commande Respectful Markets #${order.id.slice(0, 8)}`,
      pickup_point_code: order.relay_point?.code || '',
      confirm: false,
    }));
    if (offers.length === 0) {
      axios.get(`${API}/shipping/boxtal/offers`, authHeaders).then(({ data }) => setOffers(data.offers || [])).catch(() => {});
      axios.get(`${API}/shipping/boxtal/content-categories`, authHeaders)
        .then(({ data }) => setCategories(data.categories || []))
        .catch(() => setCategories([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  if (!order) return null;

  const selectedOffer = offers.find((o) => o.code === form.offer_code);
  const offersByCarrier = offers.reduce((acc, o) => {
    (acc[o.carrier] = acc[o.carrier] || []).push(o);
    return acc;
  }, {});

  const handleCreate = async () => {
    if (!form.offer_code) return toast.error(T('Choisissez une offre de transport', 'Pick a shipping offer'));
    if (!form.category_id) return toast.error(T('Choisissez une catégorie de contenu', 'Pick a content category'));
    if (!form.confirm) return toast.error(T('Veuillez confirmer la facturation Boxtal', 'Please confirm Boxtal billing'));
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${order.id}/boxtal/ship`, {
        shipping_offer_code: form.offer_code,
        confirm: true,
        package: {
          weight_kg: parseFloat(form.weight_kg),
          length_cm: parseInt(form.length_cm, 10),
          width_cm: parseInt(form.width_cm, 10),
          height_cm: parseInt(form.height_cm, 10),
          value_eur: parseFloat(form.value_eur),
        },
        content_category_id: form.category_id,
        content_description: form.description,
        from_address: fromAddr,
        pickup_point_code: form.pickup_point_code || null,
      }, authHeaders);
      setBoxtal(data.boxtal);
      onUpdated?.(order.id, { boxtal: data.boxtal });
      toast.success(T('Expédition Boxtal créée !', 'Boxtal shipment created!'));
    } catch (error) {
      toast.error(errText(error));
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders/${order.id}/boxtal`, authHeaders);
      setBoxtal(data.boxtal);
      setTracking(data.tracking || null);
      onUpdated?.(order.id, { boxtal: data.boxtal, tracking: data.tracking, status: data.order_status });
      toast.success(T('Statut actualisé', 'Status refreshed'));
    } catch (error) {
      toast.error(errText(error));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(T('Annuler cette expédition Boxtal ?', 'Cancel this Boxtal shipment?'))) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/orders/${order.id}/boxtal`, authHeaders);
      setBoxtal(data.boxtal);
      onUpdated?.(order.id, { boxtal: data.boxtal });
      toast.success(T('Expédition annulée', 'Shipment cancelled'));
    } catch (error) {
      toast.error(errText(error));
    } finally {
      setBusy(false);
    }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setFA = (k) => (e) => setFromAddr({ ...fromAddr, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="boxtal-ship-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#0F4C5C]" />
            {T('Expédition Boxtal', 'Boxtal Shipment')} — #{order.id.slice(0, 8)}
          </DialogTitle>
          <DialogDescription>
            {order.shipping_address?.first_name} {order.shipping_address?.last_name} · {order.shipping_address?.city}, {order.shipping_address?.country}
            {order.relay_point?.name && ` · ${T('Point relais', 'Pickup point')}: ${order.relay_point.name}`}
          </DialogDescription>
        </DialogHeader>

        {boxtal?.shipping_order_id && boxtal.status !== 'CANCELLED' ? (
          <div className="space-y-4" data-testid="boxtal-status-view">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground">{T('Référence Boxtal', 'Boxtal reference')}</p>
                <p className="font-mono font-semibold" data-testid="boxtal-order-id">{boxtal.shipping_order_id}</p>
              </div>
              <Badge className="badge-info" data-testid="boxtal-order-status">{boxtal.status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">{T('Offre', 'Offer')}:</span> {boxtal.offer_code}</div>
              <div><span className="text-muted-foreground">{T('Prise en charge', 'Taking over')}:</span> {boxtal.expected_taking_over_date}</div>
              {boxtal.delivery_price?.value != null && (
                <div><span className="text-muted-foreground">{T('Coût HT', 'Cost excl. tax')}:</span> €{Number(boxtal.delivery_price.value).toFixed(2)}</div>
              )}
              {boxtal.estimated_delivery_date && (
                <div><span className="text-muted-foreground">{T('Livraison estimée', 'Est. delivery')}:</span> {boxtal.estimated_delivery_date}</div>
              )}
            </div>

            {tracking?.tracking_number && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm" data-testid="boxtal-tracking-info">
                <p className="font-semibold text-emerald-800">{T('Suivi', 'Tracking')}: {tracking.tracking_number}</p>
                {tracking.message && <p className="text-emerald-700 text-xs mt-0.5">{tracking.message}</p>}
                {tracking.tracking_url && (
                  <a href={tracking.tracking_url} target="_blank" rel="noreferrer" className="text-xs underline text-emerald-700">
                    {T('Ouvrir le suivi transporteur', 'Open carrier tracking')}
                  </a>
                )}
              </div>
            )}

            {(boxtal.documents || []).length > 0 ? (
              <div className="space-y-2" data-testid="boxtal-documents-list">
                <p className="text-sm font-semibold">{T('Documents d\'expédition', 'Shipping documents')}</p>
                {boxtal.documents.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-[#0F4C5C] underline"
                    data-testid={`boxtal-document-${d.type}-${i}`}>
                    <FileText className="w-4 h-4" />
                    {d.type === 'LABEL' ? T('Étiquette d\'expédition', 'Shipping label') : d.type} ({d.format})
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {T('Les étiquettes sont générées par Boxtal sous quelques minutes. Cliquez sur « Actualiser ».',
                   'Labels are generated by Boxtal within minutes. Click "Refresh".')}
              </p>
            )}

            <div className="flex gap-2 pt-2">
              <Button onClick={handleRefresh} disabled={busy} className="btn-primary" data-testid="boxtal-refresh-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                {T('Actualiser', 'Refresh')}
              </Button>
              <Button onClick={handleCancel} disabled={busy} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="boxtal-cancel-btn">
                <XCircle className="w-4 h-4 mr-1.5" />
                {T('Annuler l\'expédition', 'Cancel shipment')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="boxtal-create-form">
            {boxtal?.status === 'CANCELLED' && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                {T('L\'expédition précédente a été annulée. Vous pouvez en créer une nouvelle.',
                   'The previous shipment was cancelled. You can create a new one.')}
              </p>
            )}
            <div>
              <Label>{T('Offre de transport', 'Shipping offer')} *</Label>
              <Select value={form.offer_code} onValueChange={(v) => setForm({ ...form, offer_code: v })}>
                <SelectTrigger className="mt-1" data-testid="boxtal-offer-select">
                  <SelectValue placeholder={T('Choisir une offre…', 'Pick an offer…')} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {Object.entries(offersByCarrier).map(([carrier, list]) => (
                    <SelectGroup key={carrier}>
                      <SelectLabel>{carrier}</SelectLabel>
                      {list.map((o) => (
                        <SelectItem key={o.code} value={o.code}>
                          {o.label}{o.relay ? ' 📍' : ''}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {selectedOffer?.relay && (
                <p className="text-xs text-amber-700 mt-1">
                  {T('Cette offre nécessite un code de point relais.', 'This offer requires a pickup point code.')}
                </p>
              )}
            </div>

            {selectedOffer?.relay && (
              <div>
                <Label>{T('Code point relais', 'Pickup point code')} *</Label>
                <Input value={form.pickup_point_code} onChange={setF('pickup_point_code')} className="mt-1" data-testid="boxtal-pickup-code" />
              </div>
            )}

            <div className="grid grid-cols-5 gap-2">
              <div>
                <Label className="text-xs">{T('Poids (kg)', 'Weight (kg)')}</Label>
                <Input value={form.weight_kg} onChange={setF('weight_kg')} className="mt-1" data-testid="boxtal-weight" />
              </div>
              <div>
                <Label className="text-xs">L (cm)</Label>
                <Input value={form.length_cm} onChange={setF('length_cm')} className="mt-1" data-testid="boxtal-length" />
              </div>
              <div>
                <Label className="text-xs">l (cm)</Label>
                <Input value={form.width_cm} onChange={setF('width_cm')} className="mt-1" data-testid="boxtal-width" />
              </div>
              <div>
                <Label className="text-xs">H (cm)</Label>
                <Input value={form.height_cm} onChange={setF('height_cm')} className="mt-1" data-testid="boxtal-height" />
              </div>
              <div>
                <Label className="text-xs">{T('Valeur €', 'Value €')}</Label>
                <Input value={form.value_eur} onChange={setF('value_eur')} className="mt-1" data-testid="boxtal-value" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{T('Catégorie de contenu', 'Content category')} *</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="boxtal-category-select">
                    <SelectValue placeholder={T('Choisir…', 'Pick…')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{T('Description du contenu', 'Content description')}</Label>
                <Input value={form.description} onChange={setF('description')} className="mt-1" data-testid="boxtal-description" />
              </div>
            </div>

            <details className="border rounded-lg p-3">
              <summary className="text-sm font-semibold cursor-pointer">
                {T('Adresse d\'expédition (expéditeur)', 'Sender address')} — {fromAddr.company}, {fromAddr.city}
              </summary>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Input value={fromAddr.company} onChange={setFA('company')} placeholder={T('Société', 'Company')} data-testid="boxtal-from-company" />
                <Input value={fromAddr.email} onChange={setFA('email')} placeholder="Email" data-testid="boxtal-from-email" />
                <Input value={fromAddr.first_name} onChange={setFA('first_name')} placeholder={T('Prénom', 'First name')} />
                <Input value={fromAddr.last_name} onChange={setFA('last_name')} placeholder={T('Nom', 'Last name')} />
                <Input value={fromAddr.phone} onChange={setFA('phone')} placeholder={T('Téléphone', 'Phone')} />
                <Input value={fromAddr.street} onChange={setFA('street')} placeholder={T('Rue', 'Street')} />
                <Input value={fromAddr.postal_code} onChange={setFA('postal_code')} placeholder={T('Code postal', 'Postal code')} />
                <Input value={fromAddr.city} onChange={setFA('city')} placeholder={T('Ville', 'City')} />
              </div>
            </details>

            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex items-start gap-2">
                <Checkbox id="boxtal-confirm" checked={form.confirm}
                  onCheckedChange={(v) => setForm({ ...form, confirm: !!v })}
                  data-testid="boxtal-confirm-checkbox" />
                <Label htmlFor="boxtal-confirm" className="text-xs font-normal cursor-pointer leading-snug">
                  {T('Je comprends que cette expédition sera commandée et facturée sur mon compte Boxtal (production).',
                     'I understand this shipment will be ordered and billed on my Boxtal account (production).')}
                </Label>
              </div>
            </div>

            <Button onClick={handleCreate} disabled={busy || !form.confirm} className="w-full btn-primary" data-testid="boxtal-create-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
              {T('Créer l\'expédition Boxtal', 'Create Boxtal shipment')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
